import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { userAPI } from '../services/api';
import SimpleAddressInput, { SimpleAddressInputRef, SimpleAddressData } from '../components/SimpleAddressInput';
import AddressMapSearch from '../components/AddressMapSearch';
import { GeocodeSuggestion } from '../services/geocoding';
import './Auth.css';

type AddressMode = 'simple' | 'advanced';

const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();

  // Address mode state
  const [addressMode, setAddressMode] = useState<AddressMode>('simple');

  const [addressData, setAddressData] = useState<SimpleAddressData | GeocodeSuggestion | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Ref for SimpleAddressInput to trigger validation
  const simpleAddressRef = useRef<SimpleAddressInputRef>(null);

  const handleAddressValidate = (data: SimpleAddressData) => {
    setAddressData(data);
    setError(''); // Clear any errors
  };

  const handleAdvancedAddressSelect = (address: GeocodeSuggestion) => {
    setAddressData(address);
    setError(''); // Clear any errors
  };

  const handleSwitchToAdvanced = () => {
    setAddressMode('advanced');
  };

  const handleSwitchToSimple = () => {
    setAddressMode('simple');
    setAddressData(null); // Clear address data when switching back
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // For simple mode, trigger validation
    if (addressMode === 'simple') {
      if (!simpleAddressRef.current) {
        setError('Erreur interne: référence au formulaire d\'adresse non disponible');
        return;
      }

      setLoading(true);

      // Validate address via geocoding
      const isValid = await simpleAddressRef.current.validateAddress();

      if (!isValid) {
        setLoading(false);
        // Error message is already set by SimpleAddressInput
        return;
      }

      // Address data will be set via handleAddressValidate callback
    }

    // Check if address data is available (for both modes)
    if (!addressData) {
      setError('Veuillez sélectionner ou valider une adresse');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Prepare address data with all necessary fields
      const addressUpdateData = {
        street_address: addressData.street_address,
        city: addressData.city,
        postal_code: addressData.postal_code,
        country: addressData.country,
        state: addressData.state,
        latitude: addressData.latitude,
        longitude: addressData.longitude,
        street_number: (addressData as any).street_number,
        street_name: (addressData as any).street_name,
        district: (addressData as any).district,
      };

      // Send address data to backend
      await userAPI.updateAddress(addressUpdateData);

      // Refresh user data to include the new address
      if (user?.id) {
        const { user: updatedUser } = await userAPI.getUserById(user.id);
        // Update the auth context with the new user data
        updateUser(updatedUser);
      }

      // Redirect to address privacy setup after successful address update
      navigate('/address-privacy-setup', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || t('completeProfile.errors.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', marginBottom: '8px' }}>
            🎉 {t('completeProfile.title', { name: user?.first_name || '' })}
          </h1>
          <p className="auth-subtitle" style={{ fontSize: '16px', lineHeight: '1.5' }}>
            {t('completeProfile.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h3 className="form-section-title">
              📍 {t('completeProfile.addressTitle')}
            </h3>
            <p className="form-section-subtitle">
              {t('completeProfile.addressSubtitle')}
            </p>
          </div>

          {/* Address input - Simple or Advanced mode */}
          {addressMode === 'simple' ? (
            <SimpleAddressInput
              ref={simpleAddressRef}
              onAddressValidate={handleAddressValidate}
              onSwitchToAdvanced={handleSwitchToAdvanced}
              showAdvancedToggle={true}
            />
          ) : (
            <>
              <AddressMapSearch onAddressSelect={handleAdvancedAddressSelect} mode="high-quality" />
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button
                  type="button"
                  className="btn-toggle-simple"
                  onClick={handleSwitchToSimple}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-color, #4285f4)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  ← Revenir au mode simple
                </button>
              </div>
            </>
          )}

          <button type="submit" className="btn-primary" disabled={loading} style={{ marginTop: '20px' }}>
            {loading ? t('completeProfile.saving') : t('completeProfile.submit')}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '14px', color: 'var(--text-secondary)' }}>
          {t('completeProfile.privacy')}
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
